// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A labelled expand/collapse row for detail a surface should keep reachable
 * without spending its first screen on it.
 *
 * Native `<details>`/`<summary>` rather than a controlled pair: the disclosure
 * semantics, keyboard handling, and Chrome's find-in-page auto-expansion all
 * come for free, and collapsed content that find-in-page can still reach is the
 * property that makes hiding it safe.
 */
export interface DisclosureProps {
  readonly label: string;
  /** Secondary text on the summary row — what the reader gets by opening it. */
  readonly hint?: string;
  readonly defaultOpen?: boolean;
  /**
   * Mount the content on first open rather than up front, for content that
   * fetches. Find-in-page cannot reach it until then, so keep it for panels
   * whose cost is the reason they are collapsed.
   */
  readonly lazy?: boolean;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

export function Disclosure({
  label,
  hint,
  defaultOpen = false,
  lazy = false,
  children,
  className,
  testId,
}: DisclosureProps): React.ReactElement {
  const [opened, setOpened] = React.useState(defaultOpen);
  return (
    <details
      onToggle={(event) => {
        if (event.currentTarget.open) setOpened(true);
      }}
      // Spread rather than `open={defaultOpen}`: `open` is the controlled prop,
      // and passing it every render would fight a reader who collapses the row.
      {...(defaultOpen ? { open: true } : {})}
      className={cn('group rounded-gl border border-border', className)}
      data-testid={testId}
    >
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden rounded-gl px-3 py-2 text-sm font-medium text-foreground select-none hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-9">
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground transition-transform duration-150 ease-gl group-open:rotate-90 motion-reduce:transition-none"
        />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {hint !== undefined && (
          <span className="hidden shrink-0 text-xs font-normal text-muted-foreground sm:inline">
            {hint}
          </span>
        )}
      </summary>
      <div className="border-t border-border px-3 py-3">{!lazy || opened ? children : null}</div>
    </details>
  );
}
